import React, { useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useStores } from '../../hooks/useStores';
import styles from './UserMenu.module.css';

const UserMenu = observer(() => {
    const { auth } = useStores();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);
    
    // Закрытие меню при клике вне его
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);
    
    if (!auth.user) {
        return null;
    }
    
    const handleProfile = () => {
        navigate('/profile');
        setIsOpen(false);
    };
    
    const handleLogout = async () => {
        await auth.logout();
        navigate('/login');
        setIsOpen(false);
    };
    
    const getUserDisplayName = () => {
        if (auth.user.fullName) {
            return auth.user.fullName;
        }
        if (auth.user.firstName || auth.user.lastName) {
            return `${auth.user.firstName || ''} ${auth.user.lastName || ''}`.trim();
        }
        return auth.user.username;
    };
    
    const getUserInitials = () => {
        const displayName = getUserDisplayName();
        if (!displayName) {
            return 'U';
        }
        const parts = displayName.split(' ');
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return displayName.substring(0, 2).toUpperCase();
    };
    
    return (
        <div className={styles.userMenu} ref={menuRef}>
            <button
                className={styles.userMenuTrigger}
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={() => setIsOpen(true)}
            >
                {auth.user.avatar ? (
                    <img 
                        src={auth.user.avatar} 
                        alt={getUserDisplayName()} 
                        className={styles.userAvatar}
                    />
                ) : (
                    <div className={styles.userAvatarPlaceholder}>
                        {getUserInitials()}
                    </div>
                )}
                <span className={styles.userName}>{getUserDisplayName()}</span>
                <svg 
                    className={`${styles.dropdownArrow} ${isOpen ? styles.open : ''}`}
                    width="12" 
                    height="12" 
                    viewBox="0 0 12 12"
                >
                    <path d="M6 8L2 4h8z" fill="currentColor"/>
                </svg>
            </button>
            
            {isOpen && (
                <div className={styles.userMenuDropdown}>
                    <div className={styles.userMenuHeader}>
                        <div className={styles.userMenuInfo}>
                            <div className={styles.userMenuName}>{getUserDisplayName()}</div>
                            <div className={styles.userMenuEmail}>{auth.user.email}</div>
                            <div className={styles.userMenuRole}>{auth.user.position || auth.user.role}</div>
                        </div>
                    </div>
                    
                    <div className={styles.userMenuDivider}></div>
                    
                    <button 
                        className={styles.userMenuItem}
                        onClick={handleProfile}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                        </svg>
                        <span>Мой профиль</span>
                    </button>
                    
                    <button 
                        className={`${styles.userMenuItem} ${styles.logout}`}
                        onClick={handleLogout}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path fillRule="evenodd" d="M10 12.5a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v2a.5.5 0 0 0 1 0v-2A1.5 1.5 0 0 0 9.5 2h-8A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-2a.5.5 0 0 0-1 0v2z"/>
                            <path fillRule="evenodd" d="M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3z"/>
                        </svg>
                        <span>Выйти</span>
                    </button>
                </div>
            )}
        </div>
    );
});

export default UserMenu;